import React from 'react';
import { PropTypes } from "prop-types";
import styled, { useTheme } from "styled-components";
import { getTranslation } from '../translations/translationUtils';
import { formatDateButtonDate, formatItineraryDate } from "../utils/dateTimeUtils";

const WEATHER_ICON_MAP = {
  '01d': '01d',
  '01n': '01n',
  '02d': '02d',
  '02n': '02n',
  '03d': '03',
  '03n': '03',
  '04d': '04',
  '04n': '04',
  '09d': '09',
  '09n': '09',
  '10d': '10',
  '10n': '10',
  '11d': '11',
  '11n': '11',
  '13d': '13',
  '13n': '13',
  '50d': '50',
  '50n': '50',
};

export const getWeatherIcon = (icon) => {
  if (!icon) return "";
  return WEATHER_ICON_MAP[icon] || icon;
};

const ThemedCardContainer = styled.div`
  box-shadow: ${props => props.theme.boxShadow || "0 4px 12px rgba(0, 0, 0, 0.08)"};
`;

const ThemedBadge = styled.span`
  background-color: ${props => props.theme.buttonBackground || props.theme.headerColor || "#6B207D"};
  color: #ffffff;
`;

const ThemedDestinationName = styled.h2`
  color: ${props => props.theme.bodyColor || "#333333"};
`;

const ThemedTempTextLg = styled.div`
  color: ${props => props.theme.bodyColor || "#333333"};
`;

const ThemedSynopsisText = styled.div`
  color: ${props => props.theme.bodyColor || "#333333"};
`;

const ThemedMetricValue = styled.div`
  color: ${props => props.theme.bodyColor || "#333333"};
`;

const ThemedUnavailableTitle = styled.div`
  color: ${props => props.theme.bodyColor || "#333333"};
`;


const WeatherForecastInformation = ({ schedule, forecastData, isToday }, context) => {
  const theme = useTheme();

  const title = getTranslation(schedule.title);

  let timeStr = "";
  if (schedule.atSea) {
    timeStr = context.t("weather-at-sea");
  }

  const scheduleData = Array.isArray(forecastData) ? forecastData[0] : forecastData;
  const forecastList = scheduleData?.weather?.forecast || [];
  const hasForecast = forecastList.length > 0;

  const todayWeather = isToday ? forecastList.find(slot => slot.isActiveVesselWindow) : null;

  const dateStrShort = formatDateButtonDate(schedule.date, theme.dateLocale);
  const dateStrLong = formatItineraryDate(schedule.date, theme.dateLocale);

  const unit = todayWeather?.unit || "metric";
  const tempUnit = unit === "imperial" ? "°F" : "°C";
  const windUnit = unit === "imperial" ? "mph" : "km/h";

  return (
    <ThemedCardContainer className="weather-card-container">
      <div className={`weather-left-panel ${isToday ? "is-today" : ""}`}>
        {isToday && (
          <ThemedBadge className="weather-badge">
            {context.t("weather_today")}
          </ThemedBadge>
        )}
        
        <div className="weather-mobile-header-wrapper">
          <ThemedDestinationName className="weather-destination-name">{title}</ThemedDestinationName>
          <div className="weather-mobile-subtitle">
            {dateStrShort} {timeStr && `· ${timeStr}`}
          </div>
        </div>

        <div className="weather-desktop-header-wrapper">
          <div className="weather-desktop-date">{dateStrLong}</div>
          <ThemedDestinationName className="weather-destination-name">{title}</ThemedDestinationName>
          <div className="weather-desktop-hours">{timeStr}</div>
        </div>
      </div>

      <div className="weather-right-panel">
        {isToday && todayWeather ? (
          <div className="weather-current-conditions-wrapper">
            <div>
              <div className="weather-conditions-title">
                {context.t("weather_current_conditions")}
              </div>
              <div className="weather-current-main">
                <img
                  className="weather-icon-lg"
                  src={`/images/weather/${getWeatherIcon(todayWeather.icon)}.svg`}
                  alt={todayWeather.synopsis}
                />
                <ThemedTempTextLg className="weather-temp-text-lg">{Math.round(todayWeather.temperature)}{tempUnit}</ThemedTempTextLg>
                <div className="weather-synopsis-wrapper">
                  <ThemedSynopsisText className="weather-synopsis-text">{todayWeather.synopsis}</ThemedSynopsisText>
                  <div className="weather-feels-like-text">
                    {context.t("feelslike") || "Feels Like"} {Math.round(todayWeather.feelsLike)}{tempUnit}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="weather-metrics-container">
              <div className="weather-metric-box">
                <ThemedMetricValue className="weather-metric-value">{todayWeather.humidity}%</ThemedMetricValue>
                <div className="weather-metric-label">{context.t("humidity")}</div>
              </div>
              <div className="weather-metric-box">
                <ThemedMetricValue className="weather-metric-value">
                  {todayWeather.windSpeed} {windUnit}
                </ThemedMetricValue>
                <div className="weather-metric-label">{context.t("wind")}</div>
              </div>
              <div className="weather-metric-box">
                <ThemedMetricValue className="weather-metric-value">{todayWeather.pressure}</ThemedMetricValue>
                <div className="weather-metric-label">{context.t("weather_pressure")}</div>
              </div>
            </div>
          </div>
        ) : null}

        {hasForecast ? (
          <>
            <div className="weather-section-header">
              {context.t("weather_forecast")}
            </div>
            <div className="weather-slots-scroll-container">
              {forecastList.map(slot => (
                  <div
                    key={slot.id}
                    className="weather-slot-card"
                  >
                    <div className="weather-slot-time">{slot.time}</div>
                    <img
                      className="weather-slot-icon"
                      src={`/images/weather/${getWeatherIcon(slot.icon)}.svg`}
                      alt={slot.synopsis}
                    />
                    <div className="weather-slot-temp">{Math.round(slot.temperature)}°</div>
                  </div>
                ))}
            </div>
          </>
        ) : (
          <div className="weather-unavailable-container">
            <img
              className="weather-calendar-icon"
              src={require("../resources/images/daily_calendar_disabled.svg")}
              alt="Calendar Icon"
            />
            <div className="weather-unavailable-text-section">
              <ThemedUnavailableTitle className="weather-unavailable-title">{context.t("weather_forecast_not_yet_available")}</ThemedUnavailableTitle>
            </div>
          </div>
        )}
      </div>
    </ThemedCardContainer>
  );
};

WeatherForecastInformation.contextTypes = {
  t: PropTypes.func.isRequired
};

export default WeatherForecastInformation;
